/**
 * Store Pinia des bâtiments (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import BuildingService from "../services/BuildingService";
import { IBuilding } from "../models/building";
import { IBuildingForm } from "../models/forms/buildingForm";
import { IBuildingApiResponse } from "../models/api/buildingApiResponse";
import { IBuildingFilter } from "../models/filters/buildingFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { ICenter } from "../models/center";
import HeaderOrderViewModel from "../models/viewModels/headerOrderViewModel";
import HeaderOrientationViewModel from "../models/viewModels/headerOrientationViewModel";
import { useUsersStore } from "./users.store";
import { useCenterStore } from "./center.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

/**
 * Store Pinia des bâtiments utilisant la Composition API
 */
export const useBuildingStore = defineStore("buildings", () => {
    // State
    const datas = ref<IBuilding[]>([]);
    const current = ref<IBuilding>({} as IBuilding);
    const itemForm = ref<IBuildingForm>({} as IBuildingForm);
    const isLoading = ref(false);

    // Getters
    const getBuildingsForCurrentCenter = computed((): IBuilding[] => {
        const userStore = useUsersStore();
        return datas.value.filter(b => b.center?.id == userStore.currentCenter?.id);
    });

    const getBuildingsForCurrentCenterSorted = computed((): IBuilding[] => {
        return [...getBuildingsForCurrentCenter.value].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
    });

    const getSkippedCorridorIndexes = computed((): number[] => {
        return current.value.skippedCorridors?.length > 0 ?
            current.value.skippedCorridors.split(';').map(n => +n) : [];
    });

    const getSkippedNumbers = computed((): number[] => {
        return current.value.skippedNumbers?.length > 0 ?
            current.value.skippedNumbers.split(';').map(n => +n) : [];
    });

    const getFormSkippedCorridorIndexes = computed((): number[] => {
        return itemForm.value.skippedCorridors?.length > 0 ?
            itemForm.value.skippedCorridors.split(';').map(n => +n) : [];
    });

    const getFormSkippedNumbers = computed((): number[] => {
        return itemForm.value.skippedNumbers?.length > 0 ?
            itemForm.value.skippedNumbers.split(';').map(n => +n) : [];
    });

    const isCorridorOrderAscending = computed((): boolean => {
        return current.value.corridorOrder === 0;
    });

    const isFormCorridorOrderAscending = computed((): boolean => {
        return itemForm.value.corridorOrder === 0;
    });

    const isNumberOrderAscending = computed((): boolean => {
        return current.value.numberOrder === 0;
    });

    const isFormNumberOrderAscending = computed((): boolean => {
        return itemForm.value.numberOrder === 0;
    });

    const getCorridorOrderOptions = computed(() => {
        return [
            { id: 0, label: "Ordre alphabétique" },
            { id: 1, label: "Ordre alphabétique inverse" }
        ] as HeaderOrderViewModel[];
    });

    const getNumberOrderOptions = computed(() => {
        return [
            { id: 0, label: "Ordre numérique croissant" },
            { id: 1, label: "Ordre numérique décroissant" }
        ] as HeaderOrderViewModel[];
    });

    const getHeadersOrientationOptions = computed(() => {
        return [
            { id: 0, label: "Logettes" },
            { id: 1, label: "Allées" }
        ] as HeaderOrientationViewModel[];
    });

    // Actions

    /**
     * Hydrate une liste de bâtiments en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param buildings Liste de bâtiments reçus de l'API avec des IDs
     * @returns Liste de bâtiments avec les références des stores
     */
    async function hydrateBuildings(buildings: IBuildingApiResponse[]): Promise<IBuilding[]> {
        if (buildings.length === 0) return [];

        // 1. Récupérer les stores une seule fois
        const centerStore = useCenterStore();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];

        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps d'index pour des lookups O(1)
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));

        // 4. Hydrater tous les bâtiments en une seule passe
        const models: IBuilding[] = [];

        for (const building of buildings) {
            const model = { ...building } as unknown as IBuilding;

            // Lier les références (lookup O(1) via Map)
            model.center = centerMap.get(building.centerId)!;

            models.push(model);
        }

        return models;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: IBuildingFilter) {
        isLoading.value = true;
        let result = await BuildingService.getAll<IBuildingFilter, IBuildingApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateBuildings(result.data as IBuildingApiResponse[]);
        }

        return result;
    }

    /**
     * Récupération d'un bâtiment par son identifiant
     * @param id
     */
    async function get(id: number) {
        let result = await BuildingService.getById<IBuildingApiResponse>(id);

        if (!result.error) {
            const [hydratedBuilding] = await hydrateBuildings([result.data as IBuildingApiResponse]);

            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = hydratedBuilding;

            current.value = hydratedBuilding;
            itemForm.value = toForm(hydratedBuilding);
        }

        return result;
    }

    /**
     * Récupération de l'objet dans le store
     * @param id Identifiant de l'objet
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IBuilding);
        return current.value;
    }

    /**
     * Mise à jour ou création d'un objet
     * @param maj
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IBuildingApiResponse | IDataOperationResult<IBuildingApiResponse>>;

        if (itemForm.value.id > 0) {
            result = await BuildingService.update(itemForm.value);

            if (!result.error) {
                const [hydratedBuilding] = await hydrateBuildings([result.data as IBuildingApiResponse]);
                current.value = hydratedBuilding;

                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = "Item non trouvé dans le store";
                        return new ServiceOperationResult<IDataOperationResult<IBuilding>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await BuildingService.create(itemForm.value);

            if (!result.error) {
                const [hydratedBuilding] = await hydrateBuildings([result.data as IBuildingApiResponse]);
                current.value = hydratedBuilding;
            }
        }

        return result;
    }

    /**
     * Delete de l'objet
     */
    async function deleteBuilding() {
        let result = await BuildingService.delete<IBuilding>(current.value.id);

        if (!result.error) {
            let index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem();
            itemForm.value = newForm();
        }

        return result;
    }

    /**
     * Transformation de l'objet en formulaire
     * @param item Bâtiment
     */
    function toForm(item: IBuilding): IBuildingForm {
        const form = {} as IBuildingForm;
        Object.assign(form, item);

        // Clé étrangère avec fallback
        form.centerId = item.center?.id ?? (item as any).centerId;

        itemForm.value = form;
        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(): IBuildingForm {
        itemForm.value = {
            id: 0,
            description: "",
            isHorizontal: false,
            name: "",
            nbrColumns: 0,
            nbrRows: 0,
            number: 0,
            startCorridor: 0,
            skippedCorridors: "",
            corridorOrder: 0,
            startNumber: 0,
            skippedNumbers: "",
            numberOrder: 0,
            centerId: 0,
            isDisplayWorkNumber: true,
        } as IBuildingForm;
        return itemForm.value;
    }

    /**
     * Permet d'initialiser un objet vide pour les referentiels
     */
    function newItem(): IBuilding {
        current.value = {
            id: 0,
            description: "",
            isHorizontal: false,
            name: "",
            nbrColumns: 0,
            nbrRows: 0,
            number: 0,
            startCorridor: 0,
            skippedCorridors: "",
            corridorOrder: 0,
            startNumber: 0,
            skippedNumbers: "",
            numberOrder: 0,
            center: { id: 0 } as ICenter,
            isDisplayWorkNumber: true,
        } as IBuilding;

        itemForm.value = toForm(current.value);
        return current.value;
    }

    // Retourner les propriétés et méthodes publiques
    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,

        // Getters
        getBuildingsForCurrentCenter,
        getBuildingsForCurrentCenterSorted,
        getSkippedCorridorIndexes,
        getSkippedNumbers,
        getFormSkippedCorridorIndexes,
        getFormSkippedNumbers,
        isCorridorOrderAscending,
        isFormCorridorOrderAscending,
        isNumberOrderAscending,
        isFormNumberOrderAscending,
        getCorridorOrderOptions,
        getNumberOrderOptions,
        getHeadersOrientationOptions,

        // Actions
        hydrateBuildings,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteBuilding,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajBuilding", async (payload: IPayloadStore) => {
    const store = useBuildingStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateBuildings([payload.data as IBuildingApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateBuildings([payload.data as IBuildingApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
